/** Single folder item in the storage sidebar — expand/collapse, rename, delete, context menu */

import { useState, useRef, useEffect } from 'react'
import {
  ChevronRight,
  Folder,
  FolderOpen,
  MoreHorizontal,
  Pencil,
  Trash2,
  FolderPlus,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import {
  useUpdateStorageFolder,
  useDeleteStorageFolder,
  useCreateStorageFolder,
} from '../../hooks/use-storage'
import { ConfirmDialog } from '../ui/confirm-dialog'
import type { StorageFolderNode } from '../../hooks/use-storage'

interface StorageFolderNodeProps {
  folder: StorageFolderNode
  depth: number
  activeFolderId: string | null
  onSelect: (id: string | null) => void
  isDark: boolean
  canManage: boolean
}

export function StorageFolderNode({
  folder,
  depth,
  activeFolderId,
  onSelect,
  isDark,
  canManage,
}: StorageFolderNodeProps) {
  const [expanded, setExpanded] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState(folder.name)
  const [addingChild, setAddingChild] = useState(false)
  const [newChildName, setNewChildName] = useState('')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const renameRef = useRef<HTMLInputElement>(null)
  const childInputRef = useRef<HTMLInputElement>(null)
  const isActive = activeFolderId === folder.id

  const updateFolder = useUpdateStorageFolder()
  const deleteFolder = useDeleteStorageFolder()
  const createFolder = useCreateStorageFolder()

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  // Focus rename input when opened
  useEffect(() => {
    if (renaming) renameRef.current?.select()
  }, [renaming])

  // Focus child input when opened
  useEffect(() => {
    if (addingChild) childInputRef.current?.focus()
  }, [addingChild])

  const handleRenameSubmit = async () => {
    if (!canManage) return
    const name = renameValue.trim()
    if (name && name !== folder.name) {
      await updateFolder.mutateAsync({ id: folder.id, name }).catch(console.error)
    }
    setRenaming(false)
  }

  const handleDelete = () => {
    if (!canManage) return
    setMenuOpen(false)
    setDeleteConfirmOpen(true)
  }

  const confirmDelete = async () => {
    if (!canManage) return
    setDeleteConfirmOpen(false)
    if (isActive) onSelect(null)
    await deleteFolder.mutateAsync(folder.id).catch(console.error)
  }

  const handleNewSubfolder = () => {
    if (!canManage) return
    setMenuOpen(false)
    setExpanded(true)
    setAddingChild(true)
    setNewChildName('')
  }

  const handleChildSubmit = async () => {
    if (!canManage) return
    const name = newChildName.trim()
    setAddingChild(false)
    if (!name) return
    await createFolder.mutateAsync({ name, parentId: folder.id }).catch(console.error)
  }

  const hasChildren = folder.children.length > 0

  return (
    <div>
      <div
        className={cn(
          'group flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs cursor-pointer select-none',
          isActive
            ? 'bg-brand-600 text-white'
            : isDark
              ? 'text-neutral-300 hover:bg-surface-3'
              : 'text-neutral-700 hover:bg-neutral-100',
        )}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
        onClick={() => {
          onSelect(folder.id)
          setExpanded((v) => !v)
        }}
      >
        {/* Expand chevron */}
        <button
          className={cn('shrink-0 rounded p-0.5', !hasChildren && 'invisible')}
          onClick={(e) => {
            e.stopPropagation()
            setExpanded((v) => !v)
          }}
        >
          <ChevronRight className={cn('h-3 w-3 transition-transform', expanded && 'rotate-90')} />
        </button>

        {/* Folder icon */}
        {expanded && hasChildren ? (
          <FolderOpen className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <Folder className="h-3.5 w-3.5 shrink-0" />
        )}

        {/* Name or rename input */}
        {renaming ? (
          <input
            ref={renameRef}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRenameSubmit()
              if (e.key === 'Escape') {
                setRenaming(false)
                setRenameValue(folder.name)
              }
              e.stopPropagation()
            }}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              'flex-1 rounded px-1 py-0 text-xs outline-none',
              isDark ? 'bg-surface-3 text-neutral-100' : 'bg-white text-neutral-900',
            )}
          />
        ) : (
          <span className="flex-1 truncate">{folder.name}</span>
        )}

        {/* Context menu button */}
        {canManage && (
          <div className="relative" ref={menuRef}>
            <button
              aria-label={`Folder actions for ${folder.name}`}
              onClick={(e) => {
                e.stopPropagation()
                setMenuOpen((v) => !v)
              }}
              className={cn(
                'shrink-0 rounded p-0.5 opacity-0 group-hover:opacity-100',
                isActive
                  ? 'text-white/70 hover:text-white'
                  : isDark
                    ? 'text-neutral-500 hover:text-neutral-300'
                    : 'text-neutral-400 hover:text-neutral-700',
              )}
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </button>

            {menuOpen && (
              <div
                className={cn(
                  'absolute left-0 top-full z-50 mt-0.5 min-w-[140px] rounded-lg border py-1 shadow-lg',
                  isDark ? 'border-white/[0.08] bg-surface-2' : 'border-neutral-200 bg-white',
                )}
              >
                {[
                  {
                    icon: Pencil,
                    label: 'Rename',
                    action: () => {
                      setMenuOpen(false)
                      setRenaming(true)
                    },
                  },
                  { icon: FolderPlus, label: 'New subfolder', action: handleNewSubfolder },
                  { icon: Trash2, label: 'Delete', action: handleDelete, danger: true },
                ].map(({ icon: Icon, label, action, danger }) => (
                  <button
                    key={label}
                    onClick={(e) => {
                      e.stopPropagation()
                      action()
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-1.5 text-xs',
                      danger
                        ? 'text-red-400 hover:bg-red-500/10'
                        : isDark
                          ? 'text-neutral-300 hover:bg-surface-3'
                          : 'text-neutral-700 hover:bg-neutral-100',
                    )}
                  >
                    <Icon className="h-3 w-3" />
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div>
          {folder.children.map((child) => (
            <StorageFolderNode
              key={child.id}
              folder={child}
              depth={depth + 1}
              activeFolderId={activeFolderId}
              onSelect={onSelect}
              isDark={isDark}
              canManage={canManage}
            />
          ))}
        </div>
      )}

      {/* Inline new subfolder input */}
      {canManage && addingChild && (
        <div
          className="flex items-center gap-1.5 py-1"
          style={{ paddingLeft: `${8 + (depth + 1) * 16}px` }}
        >
          <Folder className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
          <input
            ref={childInputRef}
            value={newChildName}
            onChange={(e) => setNewChildName(e.target.value)}
            onBlur={handleChildSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleChildSubmit()
              if (e.key === 'Escape') setAddingChild(false)
            }}
            placeholder="Folder name"
            className={cn(
              'flex-1 rounded px-1.5 py-0.5 text-xs outline-none',
              isDark
                ? 'bg-surface-3 text-neutral-100 placeholder-neutral-500'
                : 'bg-white text-neutral-900 placeholder-neutral-400',
            )}
          />
        </div>
      )}

      {/* Delete folder confirm dialog */}
      {canManage && (
        <ConfirmDialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          onConfirm={confirmDelete}
          title="Delete folder"
          description={`Delete "${folder.name}"? Files inside will be moved to root.`}
          confirmLabel="Delete"
          variant="danger"
        />
      )}
    </div>
  )
}
